// @flow

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Text, Input, Checkbox, Flex } from "@unifize/sarah";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import { arrayMove } from "@dnd-kit/sortable";
import { DndContext, closestCenter } from "@dnd-kit/core";
import Radio from "src/components/Radio";
import Switch from "src/components/Unifize/Switch";
import AddOption from "./AddOption";
import Options from "./Options";

import type { SelectFieldSettings } from "src/types";
import {
  PicklistTypeContainer,
  CheckboxContainer,
  CheckboxToggleContainer,
  RadioGroupContainer,
} from "./styles";
import * as colors from "src/styles/constants/colors";

type Props = {
  type: string,
  settings: ?SelectFieldSettings,
  saveSettings: Function,
};

const Select = ({ settings, saveSettings }: Props) => {
  const cancelRef = useRef();
  const [placeholder, setPlaceholder] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [adhoc, setAdhoc] = useState(false);
  const [multiple, setMultiple] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [isWarningModalShown, setIsWarningModalShown] = useState(false);
  const [defaultValue, setDefaultValue] = useState<string[]>([]);
  const [isCheckbox, setIsCheckbox] = useState(false);
  const [warningFor, setWarningFor] = useState<"multiple" | "adhoc">(
    "multiple",
  );

  useEffect(() => {
    if (settings) {
      const { options } = settings; // $FlowFixMe
      setOptions(options ?? []);
      setPlaceholder(settings.placeholder || "");
      setAdhoc(settings.adhoc || false);
      setMultiple(settings.multiple || false);
      setSortBy(settings.sortBy || "default");
      setDefaultValue(settings.defaultValue || []);
      setIsCheckbox(settings.isCheckbox || false);
    }
  }, [settings]);

  useEffect(() => {
    const newSettings = {
      placeholder,
      options: options ?? [],
      adhoc,
      multiple,
      sortBy,
      defaultValue,
      isCheckbox,
    };
    saveSettings(newSettings);
  }, [placeholder, options, adhoc, multiple, sortBy, defaultValue, isCheckbox]);

  useEffect(() => {
    if (!multiple && defaultValue.length > 1) {
      setDefaultValue([]);
    }
  }, [multiple]);

  const toggleCheckbox = () => {
    if (!isCheckbox) {
      setMultiple(false);
      setAdhoc(false);
      setIsCheckbox(true);
      if (options.length === 0) {
        setOptions(["Yes"]);
      }
    } else {
      setIsCheckbox(false);
    }
  };

  const handleChecked = (id: string, checked: boolean) => {
    if (id === "adhoc") {
      setAdhoc(checked);
    } else if (id === "multiple") {
      if (!multiple) {
        setMultiple(true);
      } else {
        setWarningFor("multiple");
        setIsWarningModalShown(true);
      }
    }
  };

  const handleRemoveOption = useCallback(
    (optionToRemove: string) => {
      // If currently 1 option is configured and user is trying to
      // delete that option convert it back to regular picklist
      if (options.length === 1) {
        setIsCheckbox(false);
      }
      const newOptions = options.filter((option) => option !== optionToRemove);
      setOptions(newOptions);

      // If the removed option was part of default value, remove it
      if (
        defaultValue &&
        Array.isArray(defaultValue) &&
        defaultValue.includes(optionToRemove)
      ) {
        const updated = defaultValue.filter(
          (option) => option !== optionToRemove,
        );
        setDefaultValue(updated);
      }
    },
    [options, defaultValue],
  );

  const handleAddNewOption = useCallback(
    (option: string) => {
      if (option.trim() !== "") {
        // If there are already 2 options configured convert it back to
        // a regular picklist
        if (options.length === 2) {
          setIsCheckbox(false);
        }
        setOptions((prev) => [...(prev ?? []), option]);
      }
    },
    [options.length, multiple],
  );

  const closeWarningModal = () => {
    setIsWarningModalShown(false);
  };

  const onSortOptions = ({ oldIndex, newIndex }) => {
    setOptions((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    // Extract index from stable ID format: "option-{index}"
    const oldIndex = parseInt(active.id.replace("option-", ""), 10);
    const newIndex = parseInt(over.id.replace("option-", ""), 10);
    if (!isNaN(oldIndex) && !isNaN(newIndex)) {
      onSortOptions({ oldIndex, newIndex });
    }
  };

  const onEdit = (oldOption, newOption) => {
    setOptions((prev) => {
      const index = prev.indexOf(oldOption);
      if (index !== -1) {
        return [...prev.slice(0, index), newOption, ...prev.slice(index + 1)];
      }
      return prev;
    });
  };

  const handleConfirm = () => {
    if (warningFor === "multiple") {
      if (isCheckbox) {
        setIsCheckbox(false);
        setMultiple(true);
      } else {
        setMultiple(false);
      }
    }
    closeWarningModal();
  };

  const handleSortingChange = useCallback(
    (e, value: "default" | "alphabetical") => {
      e.stopPropagation();
      setSortBy(value);
    },
    [],
  );

  return (
    <>
      <AlertDialog
        isOpen={isWarningModalShown}
        leastDestructiveRef={cancelRef}
        onClose={closeWarningModal}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Change picklist settings
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to change multi select to single select? For
              any fields where multiple values exist, it will get replaced by a
              single value.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeWarningModal}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirm} ml={3}>
                Change setting
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Flex direction="column" gap={5} padding={4}>
        <CheckboxToggleContainer>
          <Text>Convert picklist to checkbox</Text>
          <Flex gap={3}>
            <Switch
              isChecked={isCheckbox}
              onChange={toggleCheckbox}
              isDisabled={options.length > 2}
            />
            {options.length > 2 && (
              <Text color={colors.grey42}>
                <i>Can be enabled only when there are 2 options</i>
              </Text>
            )}
          </Flex>
        </CheckboxToggleContainer>

        <Flex
          direction="column"
          gap={3}
          justify="center"
          style={{
            maxWidth: "576px",
          }}
        >
          <Text weight="bold" htmlFor="placeholder">
            Placeholder
          </Text>
          <Input
            value={placeholder}
            placeholder="Enter placeholder"
            onChange={(e) => setPlaceholder(e.target.value)}
            style={{
              width: "576px",
            }}
          />
        </Flex>

        <Flex
          direction="column"
          gap={5}
          style={{
            maxWidth: "576px",
          }}
        >
          <Text weight="bold" htmlFor="options">
            Options
          </Text>
          <Flex
            direction="column"
            gap={3}
            style={{
              width: "100%",
            }}
          >
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Options
                options={options}
                handleRemoveOption={handleRemoveOption}
                isMultiple={multiple}
                setDefaultValue={setDefaultValue}
                defaultValue={defaultValue}
                onEdit={onEdit}
              />
            </DndContext>
            <AddOption
              handleAddNewOption={handleAddNewOption}
              isCheckbox={isCheckbox}
              optionsLength={options.length}
            />
          </Flex>
        </Flex>

        <PicklistTypeContainer>
          <CheckboxContainer>
            <Checkbox
              onChange={(value) => handleChecked("multiple", value)}
              checked={multiple}
              id="multiple"
              disabled={isCheckbox}
              label="Allow multiple values"
            />
          </CheckboxContainer>
          <CheckboxContainer>
            <Checkbox
              onChange={(value) => handleChecked("adhoc", value)}
              checked={adhoc}
              disabled={isCheckbox}
              id="adhoc"
              label="Allow users to add values dynamically"
            />
          </CheckboxContainer>
        </PicklistTypeContainer>

        <Flex
          direction="column"
          gap={5}
          style={{
            maxWidth: "576px",
          }}
        >
          <Text weight="bold">Sort by</Text>
          <RadioGroupContainer>
            <div onClick={(e) => handleSortingChange(e, "default")}>
              <Radio
                id="sortByDefault"
                checked={sortBy === "default"}
                handleChange={() => {}}
              />
              <span>Default</span>
            </div>
            <div onClick={(e) => handleSortingChange(e, "alphabetical")}>
              <Radio
                id="sortByAlphabetical"
                checked={sortBy === "alphabetical"}
                handleChange={() => {}}
              />
              <span>Alphabetical</span>
            </div>
          </RadioGroupContainer>
        </Flex>
      </Flex>
    </>
  );
};

export default Select;
